import type { ReactNode } from "react";
import { LinkMultipleFilled } from "@fluentui/react-icons";
import type { IEligibilitySectionDto } from "@/types/filteredContentTypes";
import { MarkdownContent } from "@/components/shared/Markdown/MarkdownContent";
import { useTranslations } from "@/hooks/useTranslations";
import styles from "./EligibilitySection.module.scss";

function getIconUrl(icon: string, mediaBaseUrl: string): string {
  if (icon.startsWith('http://') || icon.startsWith('https://') || icon.startsWith('//') || mediaBaseUrl === '') {
    return icon;
  }
  
  if (icon.startsWith('/')) {
    // Prepend mediaBaseUrl for root-relative paths
    return `${mediaBaseUrl.replace(/\/$/, '')}${icon}`;
  }
  return `${mediaBaseUrl.replace(/\/$/, '')}/${icon}`;
}

interface IEligibilitySectionProps {
  section: IEligibilitySectionDto;
  sectionId: string;
  mediaBaseUrl: string;
  copiedSectionId: string | null;
  onCopyLink: (sectionId: string) => void;
}

export function EligibilitySection({ 
  section, 
  sectionId, 
  mediaBaseUrl, 
  copiedSectionId, 
  onCopyLink 
}: IEligibilitySectionProps): ReactNode {
  const { t } = useTranslations();
  
  return (
    <section
      key={section.key}
      id={sectionId}
      className={styles.section}
    >
      <div className={styles.sectionHeader}>
        <button
          type="button"
          className={styles.copyLinkButton}
          onClick={(): void => onCopyLink(sectionId)}
          aria-label={`${t("contentPage.copyLink")} ${section.title}`}
          title={copiedSectionId === sectionId ? t("contentPage.linkCopied") : t("contentPage.copyLink")}
        >
          <LinkMultipleFilled className={styles.linkIcon} />
        </button>
        <p className="font-size-h2 font-weight-semibold">{section.title}</p>
      </div>
      
      {section.eligibilityCriteria && section.eligibilityCriteria.length > 0 && (
        <div className={styles.eligibilityItems}>
          {section.eligibilityCriteria.map((criteria) => {
            const icon = criteria.icon?.trim() ?? "";

            return (
              <div key={`${criteria.key}-${criteria.name}`} className={styles.eligibilityItem}>
                {icon && (
                  <div className={styles.itemIcon}>
                    <img 
                      src={getIconUrl(icon, mediaBaseUrl)} 
                      alt={criteria.title} 
                    />
                  </div>
                )}
                <div className={`${styles.itemContent} ${icon ? styles.itemContentWithIcon : ""}`}>
                  <p className="font-size-h4">{criteria.title}</p>
                  <MarkdownContent content={criteria.description} mediaBaseUrl={mediaBaseUrl} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
